import {
  DefaultFuelSpec,
  FUEL_GRADE_DEFAULT_VALUES,
  FuelPropertyName,
  FuelSpecIdLookup,
  FuelSpecMinMaxField,
  FuelSpecMinMaxValues,
  FuelSpecValues,
  fuelTypeProperties,
  iso_fuel_type_specs,
} from '@navstation-portal/fuel-specs/data-model/fuel-spec';
import { SelectOption } from '@navstation-portal/shared/data-model/common';
import { EntityMap } from '@ngrx/signals/entities';

export function buildFuelSpecLookupKey(fuelGrade?: string, isoFuelGrade?: string): string | undefined {
  const normalisedFuelGrade = normaliseFuelGrade(fuelGrade);
  if (!normalisedFuelGrade) return undefined;

  const normalisedIsoFuelGrade = normaliseFuelGrade(isoFuelGrade);
  if (!normalisedIsoFuelGrade || normalisedIsoFuelGrade === normalisedFuelGrade) {
    return normalisedFuelGrade;
  }

  return `${normalisedFuelGrade}::${normalisedIsoFuelGrade}`;
}

export function buildFuelSpecLookupIdMap(fuelSpecs: EntityMap<DefaultFuelSpec> | DefaultFuelSpec[]): FuelSpecIdLookup {
  const lookup: FuelSpecIdLookup = {};

  toFuelSpecsArray(fuelSpecs).forEach((fuelSpec) => {
    const fuelKey = buildFuelSpecLookupKey(fuelSpec.fuel_grade);
    if (fuelKey && !lookup[fuelKey]) {
      lookup[fuelKey] = fuelSpec._id;
    }

    const isoKey = buildFuelSpecLookupKey(fuelSpec.fuel_grade, fuelSpec.iso_fuel_grade);
    if (isoKey) {
      lookup[isoKey] = fuelSpec._id;
    }
  });

  return lookup;
}

export function getFuelSpecByLookup(
  fuelSpecLookup: FuelSpecIdLookup,
  fuelSpecs: EntityMap<DefaultFuelSpec>,
  fuelGrade?: string,
  isoFuelGrade?: string
): DefaultFuelSpec | undefined {
  const lookupKey = buildFuelSpecLookupKey(fuelGrade, isoFuelGrade);
  if (!lookupKey) return undefined;

  const fuelSpecId = fuelSpecLookup[lookupKey];
  if (!fuelSpecId) return undefined;

  return fuelSpecs[fuelSpecId];
}

export function buildFuelSpecsVm(
  fuelSpecs: EntityMap<DefaultFuelSpec>,
  fuelSpecLookup: FuelSpecIdLookup = buildFuelSpecLookupIdMap(fuelSpecs)
) {
  const getFuelSpecMinMaxValuesByGrade = (
    fuelGrade?: string,
    isoFuelGrade?: string
  ): FuelSpecMinMaxValues | undefined => getFuelSpecMinMaxValues(fuelSpecs, fuelSpecLookup, fuelGrade, isoFuelGrade);

  const getFieldLimits = (field: string, limits?: FuelSpecMinMaxValues) => {
    if (!limits) return { min: undefined, max: undefined };

    const limitKeys = FIELD_LIMIT_KEYS[field];
    if (!limitKeys) return { min: undefined, max: undefined };

    return {
      min: limits[limitKeys.min],
      max: limits[limitKeys.max],
    };
  };
  return {
    getFuelSpecValues: getFuelSpecValuesUtil,
    getFieldLimits,
    getFuelSpecMinMaxValues: getFuelSpecMinMaxValuesByGrade,
  };
}

const FIELD_LIMIT_KEYS: Partial<Record<string, { min: FuelSpecMinMaxField; max: FuelSpecMinMaxField }>> = {
  density: { min: 'density_15_min', max: 'density_15_max' },
  lcv: { min: 'lcv_min', max: 'lcv_max' },
  sulphur: { min: 'sulphur_min', max: 'sulphur_max' },
  viscosity: { min: 'viscosity_40_min', max: 'viscosity_40_max' },
};

export function getFuelSpecMinMaxValues(
  fuelSpecs: EntityMap<DefaultFuelSpec>,
  fuelSpecLookup: FuelSpecIdLookup,
  fuelGrade?: string,
  isoFuelGrade?: string
): FuelSpecMinMaxValues | undefined {
  if (!fuelGrade) return undefined;
  const spec = getFuelSpecByLookup(fuelSpecLookup, fuelSpecs, fuelGrade, isoFuelGrade);
  const valuesFromFuelSpec = spec ? extractMinMaxValues(spec) : {};
  const densityFallback = getFuelTypePropertyLimits(fuelGrade, 'density');
  const lcvFallback = getFuelTypePropertyLimits(fuelGrade, 'lcv');
  const isoFallback = getIsoFuelSpecFallback(isoFuelGrade);

  const density_15_min = numberGuard(
    valuesFromFuelSpec.density_15_min ?? isoFallback?.density_15_min ?? densityFallback?.min
  );
  const density_15_max = numberGuard(
    valuesFromFuelSpec.density_15_max ?? isoFallback?.density_15_max ?? densityFallback?.max
  );

  const lcv_min = numberGuard(valuesFromFuelSpec.lcv_min ?? lcvFallback?.min);
  const lcv_max = numberGuard(valuesFromFuelSpec.lcv_max ?? lcvFallback?.max);

  const viscosity_40_min = numberGuard(valuesFromFuelSpec.viscosity_40_min ?? isoFallback?.viscosity_40_min);
  const viscosity_40_max = numberGuard(valuesFromFuelSpec.viscosity_40_max ?? isoFallback?.viscosity_40_max);

  const sulphur_min = numberGuard(valuesFromFuelSpec.sulphur_min ?? isoFallback?.sulphur_min);
  const sulphur_max = numberGuard(valuesFromFuelSpec.sulphur_max ?? isoFallback?.sulphur_max);

  const mergedValues = compactFuelSpecMinMaxValues({
    ...valuesFromFuelSpec,
    density_15_min,
    density_15_max,
    lcv_min,
    lcv_max,
    viscosity_40_min,
    viscosity_40_max,
    sulphur_min: sulphur_min ?? 0,
    sulphur_max: sulphur_max ?? 3.5,
  });

  return Object.keys(mergedValues).length === 0 ? undefined : mergedValues;
}

const numberGuard = (value: unknown | null | undefined) => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value as number;
  }
  return undefined;
};

const extractMinMaxValues = (spec: DefaultFuelSpec): FuelSpecMinMaxValues => {
  return compactFuelSpecMinMaxValues({
    viscosity_40_max: numberGuard(spec.viscosity_40_max),
    viscosity_40_min: numberGuard(spec.viscosity_40_min),
    sulphur_max: numberGuard(spec.sulphur_max),
    sulphur_min: numberGuard(spec.sulphur_min),
    density_15_max: numberGuard(spec.density_15_max),
  });
};

const compactFuelSpecMinMaxValues = (values: FuelSpecMinMaxValues) => {
  return Object.fromEntries(Object.entries(values).filter(([, value]) => value !== undefined)) as FuelSpecMinMaxValues;
};

const normaliseFuelGrade = (fuelGrade?: string) => (fuelGrade || '').toLowerCase();

const getFuelTypePropertyLimits = (fuelGrade?: string, property?: FuelPropertyName) => {
  if (!property) return undefined;
  const normalisedFuelGrade = normaliseFuelGrade(fuelGrade);
  return !normalisedFuelGrade ? undefined : fuelTypeProperties[normalisedFuelGrade]?.[property];
};

const getIsoFuelSpecFallback = (isoFuelGrade?: string): FuelSpecMinMaxValues | undefined => {
  if (!isoFuelGrade) return undefined;
  const normalisedIso = normaliseFuelGrade(isoFuelGrade);
  const isoSpec = normalisedIso ? iso_fuel_type_specs[normalisedIso as keyof typeof iso_fuel_type_specs] : undefined;
  if (!isoSpec) return undefined;
  const values = compactFuelSpecMinMaxValues({
    viscosity_40_max: numberGuard(isoSpec.max_viscosity),
    viscosity_40_min: numberGuard(isoSpec.min_viscosity),
    density_15_max: numberGuard(isoSpec.max_density),
    sulphur_max: numberGuard(isoSpec.max_sulphur),
    sulphur_min: numberGuard(isoSpec.min_sulphur),
  });
  return Object.keys(values).length > 0 ? values : undefined;
};

const toFuelSpecsArray = (fuelSpecs: EntityMap<DefaultFuelSpec> | DefaultFuelSpec[]): DefaultFuelSpec[] => {
  return Array.isArray(fuelSpecs) ? fuelSpecs : Object.values(fuelSpecs);
};

export function getFuelSpecValuesUtil(
  fuelType: SelectOption | undefined,
  isoFuelGrade: SelectOption | undefined,
  fuelSpecLookup: FuelSpecIdLookup,
  fuelSpecs: EntityMap<DefaultFuelSpec>
): FuelSpecValues | undefined {
  const lookedUpFuelSpec = getFuelSpecByLookup(
    fuelSpecLookup,
    fuelSpecs,
    fuelType?.code as string | undefined,
    isoFuelGrade?.code as string | undefined
  );

  if (lookedUpFuelSpec) {
    return {
      density: lookedUpFuelSpec.density,
      lcv: lookedUpFuelSpec.lcv,
      sulphur: lookedUpFuelSpec?.imo_spec?.sulphur,
      viscosity: lookedUpFuelSpec.viscosity,
      eValue: lookedUpFuelSpec?.feum_spec?.e_value,
      wttEmissionFactor: lookedUpFuelSpec?.feum_spec?.wtt_emission_factor,
      cfCo2: lookedUpFuelSpec?.feum_spec?.cf_co2,
      cfCh4: lookedUpFuelSpec?.feum_spec?.cf_ch4,
      cfN2o: lookedUpFuelSpec?.feum_spec?.cf_n2o,
    };
  }

  // No live fuel-spec record for this grade — fall back to V1's hardcoded per-fuel-grade
  // defaults rather than leaving every field blank (see FUEL_GRADE_DEFAULT_VALUES doc comment).
  const normalisedFuelGrade = normaliseFuelGrade(fuelType?.code as string | undefined);
  return normalisedFuelGrade in FUEL_GRADE_DEFAULT_VALUES ? FUEL_GRADE_DEFAULT_VALUES[normalisedFuelGrade] : undefined;
}
